import {
  UpdateCustomerRequest,
  UpdateLeadRequest,
  UpdateEmails,
  UpdateJourneyRequest,
  UpdateJourneyStatusRequest,
} from "@/modules/common/dtos/index.v2";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import {
  BULK_MOVE_LOCK_MESSAGE,
  getErrorMessage,
  isBulkMoveConflictError,
  showToast,
} from "@/modules/common/utils";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import useEditAd from "@/modules/lead-page/hooks/lead-info/edit-ad.hook";
import useEditAdset from "@/modules/lead-page/hooks/lead-info/edit-adset.hook";
import useEditAdterm from "@/modules/lead-page/hooks/lead-info/edit-adterm.hook";
import useEditAssignedUser from "@/modules/lead-page/hooks/lead-info/edit-assigned-user.hook";
import useEditBestTimeToCall from "@/modules/lead-page/hooks/lead-info/edit-best-time-to-call.hook";
import useEditBudget from "@/modules/lead-page/hooks/lead-info/edit-budget.hook";
import useEditCampaignLocation from "@/modules/lead-page/hooks/lead-info/edit-campagin-location.hook";
import useEditCampaign from "@/modules/lead-page/hooks/lead-info/edit-campaign.hook";
import useEditDateOfLastContact from "@/modules/lead-page/hooks/lead-info/edit-date-of-last-contact.hook";
import useEditDateOfLastRequest from "@/modules/lead-page/hooks/lead-info/edit-date-of-last-request.hook";
import useEditDescription from "@/modules/lead-page/hooks/lead-info/edit-description.hook";
import useEditEmail from "@/modules/lead-page/hooks/lead-info/edit-email.hook";
import useEditLocation from "@/modules/lead-page/hooks/lead-info/edit-location.hook";
import useEditMethodOfContact from "@/modules/lead-page/hooks/lead-info/edit-method-of-contact.hook";
import useEditName from "@/modules/lead-page/hooks/lead-info/edit-name.hook";
import useEditTelephone from "@/modules/lead-page/hooks/lead-info/edit-telephone.hook";
import useEditStatus from "@/modules/lead-page/hooks/lead-info/status/edit-status.hook";
import { queryClient } from "@/queryClient";
import { UUID } from "crypto";
import { useContext, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  addCustomerEmail,
  editCustomer,
  editJourney,
  editJourneyStatus,
  editLead,
  reassignJourneyToManager,
} from "@/modules/lead-page/api/lead-page.v2.api";
import useAuth from "@/modules/common/hooks/auth.hook";

const useLeadInfo = () => {
  const [disableSuccessToast, setDisableSuccessToast] =
    useState<boolean>(false);
  const { lead } = useContext(LeadPageContext);
  const { email } = useEditEmail();
  const { location } = useEditLocation();
  const { campaignLocation } = useEditCampaignLocation();
  const { status } = useEditStatus();

  const { methodOfContact } = useEditMethodOfContact();
  const { description } = useEditDescription();
  const { name } = useEditName();
  const { dateOfLastRequest } = useEditDateOfLastRequest();
  const { dateOfLastContact } = useEditDateOfLastContact();

  const { assignedUser } = useEditAssignedUser();
  const { budget } = useEditBudget();
  const { bestTimeToCall } = useEditBestTimeToCall();
  const { telephone } = useEditTelephone();
  const { campaign } = useEditCampaign();
  const { ad } = useEditAd();
  const { adterm } = useEditAdterm();
  const { adset } = useEditAdset();

  const onEditSuccess = () => {
    // TODO: we need to invalidate getLead and getStagesByJourney for specific lead
    queryClient.invalidateQueries({ queryKey: ["getLead"] });
    queryClient.invalidateQueries({ queryKey: ["getComments"] });
    queryClient.invalidateQueries({ queryKey: ["getStagesByJourney"] });
    queryClient.invalidateQueries({ queryKey: ["track"] });

    email.setEditEmailModalOpen(false);
    email.setState(null);
    location.setEditLocationModalOpen(false);
    campaignLocation.setEditCampaignLocationModal(false);
    methodOfContact.setEditMethodOfContactModalOpen(false);
    description.setEditDescriptionModalOpen(false);
    status.setEditLeadStatusModalOpen(false);
    name.setEditLeadNameModalOpen(false);
    dateOfLastRequest.setEditLeadDateOfLastRequestModalOpen(false);
    dateOfLastContact.setEditLeadDateOfLastContactModalOpen(false);
    assignedUser.setEditAssignedUserModalOpen(false);
    budget.setEditBudgetModalOpen(false);
    bestTimeToCall.setEditBestTimeToCallModalOpen(false);
    campaign.setEditCampaignModalOpen(false);
    ad.setEditAdModalOpen(false);
    adterm.setEditAdtermModalOpen(false);
    adset.setEditAdsetModalOpen(false);

    if (!disableSuccessToast) {
      showToast({
        type: ToastType.SUCCESS,
        message: "Lead updated successfully",
        toastId: TOAST_IDS.EDIT_LEAD_SUCCESS,
        options: { position: "bottom-left", autoClose: 3000 },
      });
    }
  };

  const onEditError = (error: any) => {
    showToast({
      type: isBulkMoveConflictError(error)
        ? ToastType.WARNING
        : ToastType.ERROR,
      message: isBulkMoveConflictError(error)
        ? BULK_MOVE_LOCK_MESSAGE
        : error.status == 403
          ? "You are not authorized to update this lead"
          : error?.response?.data?.message || "Error updating lead",
      toastId: TOAST_IDS.EDIT_LEAD_ERROR,
      options: {
        position: "bottom-left",
        autoClose: 3000,
      },
    });
    campaign.setEditCampaignModalOpen(false);
    ad.setEditAdModalOpen(false);
    adterm.setEditAdtermModalOpen(false);
    adset.setEditAdsetModalOpen(false);
  };

  const {
    mutateAsync: editLeadMutation,
    isLoading: isEditLeadLoading,
    isError: isEditLeadError,
    isSuccess: isEditLeadSuccess,
    error: editLeadError,
  } = useMutation({
    mutationFn: ({
      leadId,
      update,
    }: {
      leadId: UUID;
      update: UpdateLeadRequest;
    }) => editLead(leadId, update),
    onSuccess: (_, variables) => {
      if (
        variables.update.archived === false ||
        variables.update.archived === true ||
        variables.update.rubbish === true ||
        variables.update.rubbish === false
      ) {
        setDisableSuccessToast(true);
        queryClient.invalidateQueries({ queryKey: ["getLead"] });
      } else {
        onEditSuccess();
      }
    },
    onError: onEditError,
  });

  const {
    mutateAsync: editCustomerMutation,
    isLoading: isEditCustomerLoading,
    isError: isEditCustomerError,
    isSuccess: isEditCustomerSuccess,
    error: editCustomerError,
  } = useMutation({
    mutationFn: ({
      customerId,
      update,
    }: {
      customerId: UUID;
      update: UpdateCustomerRequest;
    }) => editCustomer(customerId, update),
    onSuccess: (_, variables) => {
      if (variables.update.doNotCall) {
        setDisableSuccessToast(true);
        queryClient.invalidateQueries({ queryKey: ["getLead"] });
      } else {
        onEditSuccess();
      }
    },
    onError: onEditError,
  });

  const {
    mutateAsync: editJourneyMutation,
    isLoading: isEditJourneyLoading,
    isError: isEditJourneyError,
    isSuccess: isEditJourneySuccess,
    error: editJourneyError,
  } = useMutation({
    mutationFn: ({
      journeyUId,
      update,
    }: {
      journeyUId: UUID;
      update: UpdateJourneyRequest;
    }) => editJourney(journeyUId, update),
    onSuccess: (_, variables) => {
      if (
        variables.update.rubbish === true ||
        variables.update.rubbish === false
      ) {
        setDisableSuccessToast(true);
        queryClient.invalidateQueries({ queryKey: ["getLead"] });
      } else {
        onEditSuccess();
      }
    },
    onError: onEditError,
  });

  const {
    mutateAsync: editJourneyStatusMutation,
    isLoading: isEditJourneyStatusLoading,
    isError: isEditJourneyStatusError,
    isSuccess: isEditJourneyStatusSuccess,
    error: editJourneyStatusError,
  } = useMutation({
    mutationFn: ({
      journeyUId,
      update,
    }: {
      journeyUId: UUID;
      update: UpdateJourneyStatusRequest;
    }) => editJourneyStatus(journeyUId, update),
    onSuccess: onEditSuccess,
    onError: onEditError,
  });

  const {
    mutateAsync: editCustomerEmailMutation,
    isLoading: isEditCustomerEmailLoading,
    isError: isEditCustomerEmailError,
    isSuccess: isEditCustomerEmailSuccess,
    error: editCustomerEmailError,
  } = useMutation({
    mutationFn: ({
      customerId,
      update,
    }: {
      customerId: UUID;
      update: UpdateEmails;
    }) => addCustomerEmail(customerId, update),
    onSuccess: onEditSuccess,
    onError: onEditError,
  });

  const {
    mutateAsync: reassignJourneyToManagerMutation,
    isLoading: isReassignJourneyToManagerLoading,
  } = useMutation({
    mutationFn: (leadUId: string) => reassignJourneyToManager(leadUId),
    onSuccess: () => {
      // TODO: we need to invalidate getLead and getStagesByJourney for specific lead
      queryClient.invalidateQueries({ queryKey: ["getLead"] });
      queryClient.invalidateQueries({ queryKey: ["getComments"] });
      queryClient.invalidateQueries({ queryKey: ["lead-activity"] });
      queryClient.invalidateQueries({ queryKey: ["getStagesByJourney"] });
      showToast({
        type: ToastType.SUCCESS,
        message: "lead re-assigned to manager successfully",
        toastId: TOAST_IDS.REASSIGN_JOURNEY_TO_MANAGER_SUCCESS,
        options: { position: "bottom-left", autoClose: 3000 },
      });
    },
    onError: (error: {
      response: { data: { message: string; statusCode: number } };
    }) => {
      showToast({
        type: ToastType.ERROR,
        message: getErrorMessage(error),
        toastId: TOAST_IDS.REASSIGN_JOURNEY_TO_MANAGER_ERROR,
        options: {
          position: "bottom-left",
          autoClose: 3000,
        },
      });
    },
  });

  const isLoading =
    isEditLeadLoading ||
    isEditCustomerLoading ||
    isEditCustomerEmailLoading ||
    isEditJourneyLoading ||
    isEditJourneyStatusLoading;
  const isError =
    isEditLeadError ||
    isEditCustomerError ||
    isEditCustomerEmailError ||
    isEditJourneyError ||
    isEditJourneyStatusError;
  const isSuccess =
    isEditLeadSuccess ||
    isEditCustomerSuccess ||
    isEditCustomerEmailSuccess ||
    isEditJourneySuccess ||
    isEditJourneyStatusSuccess;
  const isLeadLoading = isEditLeadLoading;

  const isJourneyLoading = isEditJourneyLoading;

  const error =
    editLeadError ||
    editCustomerError ||
    editCustomerEmailError ||
    editJourneyError ||
    editJourneyStatusError;

  const handleConfirmUpdate = (updateValue: {
    lead?: UpdateLeadRequest;
    customer?: UpdateCustomerRequest;
    emails?: UpdateEmails;
    journey?: UpdateJourneyRequest;
    journeyStatus?: UpdateJourneyStatusRequest;
  }): Promise<void> => {
    return new Promise((resolve, reject) => {
      const updatePromises: Promise<any>[] = [];
      if (updateValue.lead && lead?.data?.lead?.uid) {
        updatePromises.push(
          editLeadMutation({
            leadId: lead.data.lead.uid,
            update: updateValue.lead,
          }),
        );

        if (updateValue.lead?.archived && isEditLeadSuccess) {
          setDisableSuccessToast(true);
        }
      }

      if (updateValue.customer && lead?.data?.lead?.customer_uid) {
        updatePromises.push(
          editCustomerMutation({
            customerId: lead.data.lead.customer_uid,
            update: updateValue.customer,
          }),
        );

        if (updateValue.customer.doNotCall && isEditCustomerSuccess) {
          setDisableSuccessToast(true);
        }
      }

      if (updateValue.journey && lead?.data?.lead?.journey_uid) {
        updatePromises.push(
          editJourneyMutation({
            journeyUId: lead.data.lead.journey_uid,
            update: updateValue.journey,
          }),
        );
      }

      if (updateValue.journeyStatus && lead?.data?.lead?.journey_uid) {
        updatePromises.push(
          editJourneyStatusMutation({
            journeyUId: lead.data.lead.journey_uid,
            update: updateValue.journeyStatus,
          }),
        );
      }

      if (updateValue.emails && lead?.data?.lead?.customer_uid) {
        updatePromises.push(
          editCustomerEmailMutation({
            customerId: lead.data.lead.customer_uid,
            update: updateValue.emails,
          }),
        );
      }

      if (updatePromises.length > 0) {
        Promise.all(updatePromises)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ["lead-activity"] });
            resolve();
          })
          .catch((error) => reject(error));
      } else {
        reject(new Error("Lead or Customer ID not found"));
      }
    });
  };

  const { user } = useAuth();

  const activeReferral = Array.isArray(lead?.data.lead.referral)
    ? lead?.data.lead.referral?.find((referral) => referral.isExpired === false)
    : undefined;

  const isReferrer =
    activeReferral &&
    Array.isArray(lead?.data.lead.referral) &&
    lead?.data.lead.referral?.find((referral) => referral.isExpired === false)
      ?.referrer?.uid === user?.["cognito:username"];

  const isManagerReassignmentDateValid =
    activeReferral &&
    typeof lead?.data.lead_type.managerReassignmentValidity === "number" &&
    (() => {
      const expirationDate = new Date(activeReferral.createdAt);
      expirationDate.setDate(
        expirationDate.getDate() +
          lead.data.lead_type.managerReassignmentValidity,
      );
      return expirationDate < new Date();
    })();

  const showReassignToManager =
    isReferrer &&
    isManagerReassignmentDateValid &&
    !activeReferral.isReassigned;

  return {
    email,
    campaignLocation,
    methodOfContact,
    location,
    status,
    description,
    name,
    dateOfLastRequest,
    dateOfLastContact,
    assignedUser,
    budget,
    telephone,
    bestTimeToCall,
    handleConfirmUpdate,
    isLoading,
    isError,
    isSuccess,
    error,
    lead,
    campaign,
    ad,
    adterm,
    adset,
    isLeadLoading,
    isJourneyLoading,
    editCustomerError,
    reassignJourneyToManagerMutation,
    isReassignJourneyToManagerLoading,
    showReassignToManager,
  };
};

export default useLeadInfo;
